import type {
  ArtStyleOption,
  Bundle,
  Catalog,
  ColorOption,
  Country,
  CountryGroup,
  Feature,
  FeatureTier,
  Highlight,
  HighlightGroup,
  HighlightTag,
  PlayerOption,
  Theme,
  ThemeScreenshot,
} from '../lib/domain-types';
import { sanitizeCssPaint } from '../lib/color';
import { CFG_CHROME } from '../../../theme/configurator-tokens';

/* The API response is large and loosely typed; we only read the fields we need. */
export type Raw = Record<string, unknown>;
const asArr = (v: unknown): Raw[] => (Array.isArray(v) ? (v as Raw[]) : []);
const get = (o: unknown, k: string): unknown => (o && typeof o === 'object' ? (o as Raw)[k] : undefined);

/** Active-language fallback for every localized CMS string in this module. */
const DEFAULT_LANG = 'en';

function firstBundle(node: Raw): Raw {
  const bundles = asArr(get(node, 'art_bundle'));
  return bundles[0] ?? {};
}

function toColor(skin: Raw, lang = DEFAULT_LANG): ColorOption {
  const tokens = (get(skin, 'tokens') as Record<string, string>) ?? {};
  const props = get(firstBundle(skin), 'properties');
  const name = loc(get(props, 'name'), lang) || (get(skin, 'name') as string) || 'Color';
  // Every CMS-set paint value below is sanitized before it leaves this function: it
  // reaches styled-components template interpolation raw (see PreviewStageView.tsx),
  // so a `{`/`}`/`;` in a compromised or misconfigured token could otherwise break out
  // of the CSS declaration. See sanitizeCssPaint's own doc for why this isn't a
  // colour-format whitelist (gradients/positions are legitimate token values here).
  const fallbackBg = sanitizeCssPaint(
    tokens['--bg-default'] ?? tokens['--body-bg-default'] ?? CFG_CHROME.brandBgFallback,
    CFG_CHROME.brandBgFallback,
  );
  return {
    id: String(get(skin, 'id')),
    name,
    designThemeId: (get(get(skin, 'skin_data'), 'design_theme_id') as string) ?? '',
    bg: fallbackBg,
    corner: sanitizeCssPaint(
      tokens['--primary-fill-default'] ?? CFG_CHROME.brandAccentFallback,
      CFG_CHROME.brandAccentFallback,
    ),
    topNav: sanitizeCssPaint(tokens['--top-nav-surface'] ?? fallbackBg, fallbackBg),
    bottomNav: sanitizeCssPaint(tokens['--bottom-nav-surface'] ?? fallbackBg, fallbackBg),
    tokens,
  };
}

/** One `art_bundle` entry tagged with a (skin, art-style, locale) triple carries the
 *  pre-rendered device screenshots for that combination; the group's own OWN entry (no
 *  `skin_id`, used for the theme's name/logo in {@link toTheme}) is skipped via the
 *  `skinId`/`artStyleId` null check. */
function toThemeScreenshot(entry: Raw): ThemeScreenshot | null {
  const attrs = (get(entry, 'attributes') as Raw) ?? {};
  const skinId = get(attrs, 'skin_id');
  const artStyleId = get(attrs, 'art_style_id');
  if (skinId == null || artStyleId == null) return null;
  return {
    skinId: String(skinId),
    artStyleId: String(artStyleId),
    locale: (get(attrs, 'locale') as string) ?? DEFAULT_LANG,
    mobile: (get(entry, 'mobile') as string) ?? '',
    desktop: (get(entry, 'desktop') as string) ?? '',
    iphone: (get(entry, 'iphone') as string) ?? '',
  };
}

function toTheme(group: Raw, lang = DEFAULT_LANG): Theme {
  const seed = get(group, 'site_seed') as Raw;
  const seedData = get(seed, 'seed_data') as Raw;
  // Display name + tagline come from the group's art bundle properties.
  const props = get(firstBundle(group), 'properties');
  const name = loc(get(props, 'name'), lang) || (get(group, 'name') as string) || 'Theme';
  const subtitle = loc(get(props, 'description'), lang) || (get(group, 'description') as string) || '';
  return {
    id: String(get(group, 'id')),
    name,
    subtitle,
    logo: (get(firstBundle(group), 'square_ar11_keycenter') as string) ?? '',
    seedId: Number(get(seed, 'id')),
    variantId: (get(seedData, 'design_variant_id') as string) ?? '',
    previewUrl: (get(seedData, 'preview_url') as string) ?? '',
    colors: asArr(get(group, 'site_skins')).map((s) => toColor(s, lang)),
    screenshots: asArr(get(group, 'art_bundle'))
      .map(toThemeScreenshot)
      .filter((s): s is ThemeScreenshot => s !== null),
    // The group's own art bundle carries the CMS badge: "new" | "none".
    isNew: String(get(props, 'badge') ?? '').trim().toLowerCase() === 'new',
  };
}

function toArtStyle(style: Raw): ArtStyleOption {
  const attrs = (get(style, 'attributes') as Raw) ?? {};
  const compat = asArr(get(get(style, 'art_style_data'), 'compatibility'));
  return {
    id: String(get(style, 'id')),
    name: (get(style, 'name') as string) ?? 'Style',
    image: (get(firstBundle(style), 'square_ar11_keycenter') as string) ?? '',
    sets: {
      banner: (get(attrs, 'site_art_style_banner_set_id') as string) ?? '',
      decorations: (get(attrs, 'site_art_style_decorations_set_id') as string) ?? '',
      iconography: (get(attrs, 'site_art_style_iconography_set_id') as string) ?? '',
      gameThumbnail: (get(attrs, 'site_game_thumbnail_set_id') as string) ?? '',
    },
    compatibility: compat.map((c) => ({
      seedId: Number(get(c, 'site_seed_id')),
      skinId: Number(get(c, 'site_skin_id')),
    })),
  };
}

/* ----------------------- gamification (step 2) ----------------------- */

/** Localized value: a {lang: string} map or a plain string. Picks the active
 *  language, else English, else the first non-empty value. EMPTY strings are
 *  skipped (the CMS ships `"es": ""` / `"ro": ""` placeholders that must NOT win
 *  over the populated `en`). */
function loc(v: unknown, lang = DEFAULT_LANG): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const nonEmpty = (val: unknown): string | undefined =>
      typeof val === 'string' && val.trim() !== '' ? val : undefined;
    return nonEmpty(o[lang]) ?? nonEmpty(o.en) ?? Object.values(o).map(nonEmpty).find(Boolean) ?? '';
  }
  return '';
}

/** art_bundle entries are tagged via properties.section_id. */
function sections(node: unknown, sectionId: string): Raw[] {
  return asArr(get(node, 'art_bundle')).filter((ab) => get(get(ab, 'properties'), 'section_id') === sectionId);
}
const section = (node: unknown, sectionId: string): Raw | undefined => sections(node, sectionId)[0];

function num(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) {
    return Number(v);
  }
  return undefined;
}

/*
 * Per-feature-tier PRICES are intentionally NOT read from the /configurator catalog. Every price
 * shown in the configurator is sourced from the recommended-campaigns API: the store re-prices
 * each tier from the campaign union (`catalogTierPriceMap` in `lib/campaigns.ts`, applied in the
 * store's `features` memo). So `toTier` emits the tier STRUCTURE + the CMS `showPrice` display
 * flag, but no price — there is deliberately no catalog `priceForTier` helper anymore.
 */

function toTier(tier: Raw, lang = DEFAULT_LANG): FeatureTier {
  const id = Number(get(tier, 'id'));
  const tierProps = (get(section(tier, 'tier'), 'properties') as Raw) ?? {};
  const headerProps = get(section(tier, 'highlightsHeader'), 'properties') as Raw | undefined;
  const videoProps = get(section(tier, 'video'), 'properties') as Raw | undefined;

  const highlights: Highlight[] = sections(tier, 'highlight').map((h, i) => {
    const p = get(h, 'properties');
    // The demo video is per-highlight now (not per-tier): it lives on the highlight's
    // own art bundle as `video_url`, with its length in `video_duration_s` and its
    // loop restart (seconds back from the end) in `video_loop_start_s`.
    const videoUrl = get(p, 'video_url') as string | undefined;
    return {
      id: `${id}-h${i}`,
      title: loc(get(p, 'title'), lang),
      text: loc(get(p, 'description'), lang),
      video: videoUrl
        ? { src: videoUrl, loop: true, loopStartS: num(get(p, 'video_loop_start_s')) }
        : undefined,
      videoDurationS: num(get(p, 'video_duration_s')),
    };
  });

  const videoSrc = videoProps ? (get(videoProps, 'src') as string) : undefined;

  // The level label (Off/Plus/Pro/Ultra) normally comes from the tier's art_bundle 'tier' section
  // name. RETIRED tiers (`is_active: false`) can ship WITHOUT that art bundle — which left `level`
  // empty and rendered a blank, unlabelled tab, so the retired rung looked "missing". Fall back to
  // the last word of the top-level tier name ("Tournaments Pro" → "Pro"); tier levels are always
  // single words, so last-word is safe, and it keeps `isOff` working (retired "… Off" → "Off").
  const rawTierName = (get(tier, 'name') as string | undefined) ?? '';
  const level = loc(get(tierProps, 'name'), lang) || rawTierName.split(' ').filter(Boolean).pop() || rawTierName;

  return {
    id,
    level,
    commercial: get(get(get(tier, 'tier_data'), 'attributes'), 'gamification_commercial_tier') as
      | string
      | undefined,
    // `showPrice` is a CMS display flag; the price itself is applied later from campaigns
    // (see the note above), never read from the /configurator catalog here.
    showPrice: Boolean(get(tierProps, 'showPrice')),
    description: loc(get(tierProps, 'description'), lang) || undefined,
    header: headerProps
      ? { title: loc(get(headerProps, 'title'), lang), description: loc(get(headerProps, 'description'), lang) }
      : undefined,
    video: videoSrc
      ? {
          src: videoSrc,
          loop: Boolean(get(videoProps, 'loop')),
          aspectRatio: get(videoProps, 'aspectRatio') as string | undefined,
        }
      : undefined,
    highlights,
  };
}

function toFeature(item: Raw, tiers: Raw[], lang = DEFAULT_LANG): Feature {
  const id = Number(get(item, 'id'));
  const itemSection = section(item, 'item');
  return {
    id,
    name: loc(get(get(itemSection, 'properties'), 'name'), lang) || (get(item, 'name') as string) || 'Feature',
    icon: (get(itemSection, 'logo') as string) ?? '',
    priority: Number(get(item, 'priority')),
    classId: num(get(get(item, 'item_data'), 'gamification_class_id')),
    tiers: tiers
      .filter((t) => Number(get(t, 'gamification_item_id')) === id && get(t, 'is_active') !== false)
      .sort((a, b) => Number(get(a, 'priority')) - Number(get(b, 'priority')))
      .map((t) => toTier(t, lang)),
  };
}

export function transformFeatures(raw: Raw, lang = DEFAULT_LANG): Feature[] {
  const tiers = asArr(get(raw, 'gamification_tiers'));
  return asArr(get(raw, 'gamification_items'))
    .filter((it) => get(it, 'is_active') !== false)
    .map((it) => toFeature(it, tiers, lang))
    .sort((a, b) => a.priority - b.priority);
}

function toBundle(b: Raw, currency: string, lang = DEFAULT_LANG): Bundle {
  const artEntry = section(b, 'bundle') ?? {};
  const artProps = get(artEntry, 'properties');
  const items = asArr(get(b, 'bundle_items')).map((bi) => {
    const dc = get(bi, 'discounted_cost');
    return {
      itemId: Number(get(bi, 'gamification_item_id')),
      tierId: Number(get(dc, 'gamification_tier_id')),
      cost: num(get(get(dc, 'cost'), currency)) ?? 0,
    };
  });
  return {
    id: Number(get(b, 'id')),
    name: loc(get(artEntry, 'text_name'), lang) || (get(b, 'name') as string) || 'Package',
    logo: (get(artEntry, 'logo') as string) ?? '',
    label: get(get(b, 'bundle_data'), 'label') as string | undefined,
    isSelected: Boolean(get(artProps, 'isSelected')),
    priority: Number(get(b, 'priority')),
    items,
    total: items.reduce((sum, it) => sum + it.cost, 0),
  };
}

export function transformBundles(raw: Raw, currency = 'USD', lang = DEFAULT_LANG): Bundle[] {
  return asArr(get(raw, 'gamification_bundles'))
    .filter((b) => get(b, 'is_active') !== false)
    .map((b) => toBundle(b, currency, lang))
    .sort((a, b) => a.priority - b.priority);
}

export function transformCatalog(raw: Raw, currency = 'USD', lang = DEFAULT_LANG): Catalog {
  return {
    // Hide seed groups the CMS has retired (`is_active: false`); only live themes are offered.
    themes: asArr(get(raw, 'site_seed_groups'))
      .filter((g) => get(g, 'is_active') !== false)
      .map((g) => toTheme(g, lang)),
    artStyles: asArr(get(raw, 'site_art_styles')).map(toArtStyle),
    // Features carry NO price (priced from campaigns in the store); `currency` still threads
    // into bundles, whose catalog totals are unused for display (they only seed tier picks).
    features: transformFeatures(raw, lang),
    bundles: transformBundles(raw, currency, lang),
  };
}

/* --------------------------- regions (country picker) --------------------------- */

/** Region CODES a group lists (`maps_service_identifier.operating_region_ids`). */
function regionIdsOf(group: Raw): string[] {
  const ids = get(get(group, 'maps_service_identifier'), 'operating_region_ids');
  return Array.isArray(ids) ? ids.map((code) => String(code)) : [];
}

/** Ascending CMS `priority` (absent = 0). */
const byPriority = (a: Raw, b: Raw): number => Number(get(a, 'priority') ?? 0) - Number(get(b, 'priority') ?? 0);

/** A CMS string array coerced to `string[]`, or `undefined` when the field is absent/not an array. */
const strList = (v: unknown): string[] | undefined => (Array.isArray(v) ? v.map((s) => String(s)) : undefined);

function toCountry(region: Raw, lang = DEFAULT_LANG): Country {
  const ab = asArr(get(region, 'art_bundle'))[0] ?? {};
  const rawName =
    loc(get(ab, 'text_name'), lang) || (get(region, 'name') as string) || (get(region, 'code') as string) || '';
  // Drop trailing currency hints like "International ($)".
  const name = rawName.replace(/\s*\(.*\)\s*$/, '').trim() || rawName;
  return {
    code: String(get(region, 'code')),
    name,
    currency: (get(get(region, 'region_data'), 'currency_code') as string) ?? 'USD',
    flag: (get(ab, 'logo') as string) ?? '',
    // Availability signal: the region's own `is_active` (false = not live yet).
    isActive: get(region, 'is_active') !== false,
    // Decorative only (art bundles are decorative): drives the "Soon" pill, nothing else.
    comingSoon: Boolean(get(get(ab, 'properties'), 'comingSoon')),
    // Region-allowed OWS payment methods; left undefined until the backend ships the field.
    ownerHubPaymentMethods: strList(get(get(region, 'region_data'), 'owner_hub_payment_methods')),
    // Market languages (primary at index 0); empty when absent.
    languages: strList(get(get(region, 'region_data'), 'language_codes')) ?? [],
  };
}

export function transformRegions(raw: Raw, lang = DEFAULT_LANG): {
  countries: Country[];
  groups: CountryGroup[];
} {
  const byCode = new Map(asArr(get(raw, 'operating_regions')).map((r) => [String(get(r, 'code')), r]));

  // Only ACTIVE region groups are surfaced (respect the group `is_active` flag); within
  // those, walk in CMS priority order and a region is claimed by the first group that lists
  // it (dedupe). Coming-soon markets inside an active group are still shown so buyers see
  // what is next. Availability is a PER-REGION concern too: the region's own `is_active`
  // decides selectable vs "not live yet" (see the picker), and the art bundle's `comingSoon`
  // is decorative: it only places the "Soon" pill.
  const orderedGroups = asArr(get(raw, 'operating_region_groups'))
    .filter((g) => Boolean(get(g, 'is_active')))
    .sort(byPriority);

  const countries: Country[] = [];
  const groups: CountryGroup[] = [];
  const claimed = new Set<string>();

  for (const g of orderedGroups) {
    const cs: Country[] = regionIdsOf(g)
      .map((code) => byCode.get(code))
      .filter((r): r is Raw => Boolean(r) && !claimed.has(String(get(r, 'code'))))
      .sort(byPriority)
      .map((region) => {
        const c = toCountry(region, lang);
        claimed.add(c.code);
        countries.push(c);
        return c;
      });
    if (!cs.length) continue;
    // Heading resolved to its English canonical (a stable i18n key): the picker
    // translates the known set at its render site and passes others through as-is.
    const name = loc(get(firstBundle(g), 'text_name'), 'en') || (get(g, 'name') as string) || '';
    groups.push({ name, countries: cs });
  }
  return { countries, groups };
}

/* --------------------------- traffic options (step 3 — players) --------------------------- */

/** Truthy CMS flag: the API sends real booleans (`true`) but may also ship the
 *  string `"true"`; an empty string / absent value is false. */
const truthy = (v: unknown): boolean => v === true || v === 'true';

/** Source-pill groups (`grp1_…`, `grp2_…`) probed on a traffic highlight bundle. */
const HL_GROUPS = [1, 2];
/** Items (pills) probed within each group (`grpN_item1_…` … `grpN_item10_…`). */
const HL_ITEMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Non-blank string, else undefined (CMS ships `null` / `""` placeholders). */
function url(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

/**
 * One traffic-option highlight art bundle → domain `Highlight`. A highlight is
 * either "simple" (just title + description) or carries 1-2 groups of source
 * pills, each item being `grpN_itemM_name` + `grpN_itemM_logo_url`; as many
 * pills are shown as the bundle carries data for. A group heading
 * (`grpN_title`) is emitted only when `grpN_show_title` is true. Each highlight
 * may also carry a demo clip (`video_url` + `video_duration_s`), played in the
 * centre stage exactly like the features step. `video_loop_start_s` is captured
 * but not yet used by the player (it loops the whole clip, as the features step
 * does).
 */
function toTrafficHighlight(props: Raw, idx: number, lang: string): Highlight {
  const groups: HighlightGroup[] = [];
  for (const gi of HL_GROUPS) {
    const tags: HighlightTag[] = [];
    for (const ii of HL_ITEMS) {
      const text = loc(get(props, `grp${gi}_item${ii}_name`), lang);
      const logoUrl = url(get(props, `grp${gi}_item${ii}_logo_url`));
      // Show a pill for every item carrying a name OR a logo.
      if (text === '' && !logoUrl) continue;
      tags.push({ text, logo: logoUrl ? { bg: 'transparent', image: logoUrl } : undefined });
    }
    if (tags.length === 0) continue;
    const name = truthy(get(props, `grp${gi}_show_title`)) ? loc(get(props, `grp${gi}_title`), lang) : '';
    groups.push({ name, tags });
  }

  const videoUrl = url(get(props, 'video_url'));
  const base: Highlight = {
    id: `h${idx}`,
    title: loc(get(props, 'title'), lang),
    text: loc(get(props, 'description'), lang),
    video: videoUrl
      ? { src: videoUrl, loop: true, loopStartS: num(get(props, 'video_loop_start_s')) }
      : undefined,
    videoDurationS: num(get(props, 'video_duration_s')),
  };

  // A single untitled group renders better as flat (ungrouped) pills — no empty heading.
  if (groups.length === 1 && !groups[0].name) {
    return { ...base, tags: groups[0].tags };
  }
  return groups.length > 0 ? { ...base, groups } : base;
}

/**
 * One traffic option resolved for the selected market → domain `PlayerOption`.
 * The cover art bundle (the one whose `properties.country_code` matches the
 * market) carries the card name / image / eyebrow + recommended flag; every
 * other art bundle is a highlight.
 */
function toPlayerOption(option: Raw, cover: Raw, highlights: Raw[], index: number, lang: string): PlayerOption {
  const coverProps = (get(cover, 'properties') as Raw) ?? {};
  return {
    id: String(get(option, 'id')),
    // Eyebrow ("OPTION 1") is the cover's localized `description`; fall back to the
    // positional chrome when the CMS leaves it blank.
    label: loc(get(coverProps, 'description'), lang) || `OPTION ${index + 1}`,
    title: loc(get(coverProps, 'name'), lang) || (get(option, 'name') as string) || '',
    // Card image: the cover art bundle's own top-level `cover_url` (NOT the one
    // nested in `properties`, which the CMS leaves null).
    image: url(get(cover, 'cover_url')) ?? '',
    recommended: truthy(get(coverProps, 'show_recommended')),
    highlights: highlights.map((h, i) => toTrafficHighlight((get(h, 'properties') as Raw) ?? {}, i, lang)),
  };
}

/**
 * Step 3 (Players) options for the selected market. Each traffic option carries
 * an `art_bundle` list: the entry tagged with the selected `country_code` is the
 * market cover (name / image / eyebrow / recommended) — its absence means the
 * option isn't offered there — and every untagged entry is a highlight. Returns
 * [] when the catalog has no traffic options for the market.
 */
export function transformTrafficOptions(raw: Raw, regionCode: string, lang = DEFAULT_LANG): PlayerOption[] {
  if (!regionCode) return [];
  return asArr(get(raw, 'traffic_options'))
    // Hidden when `is_active` is explicitly false.
    .filter((o) => get(o, 'is_active') !== false)
    .map((o) => {
      const bundles = asArr(get(o, 'art_bundle'));
      return {
        o,
        // Cover for this market; absent → option not offered here.
        cover: bundles.find((ab) => get(get(ab, 'properties'), 'country_code') === regionCode),
        // Highlights are the bundles that aren't a country cover, in catalog order.
        highlights: bundles.filter((ab) => !get(get(ab, 'properties'), 'country_code')),
        prio: num(get(o, 'priority')) ?? 0,
      };
    })
    .filter((x): x is { o: Raw; cover: Raw; highlights: Raw[]; prio: number } => x.cover != null)
    // Ordered by `priority` ascending (0 = first).
    .sort((a, b) => a.prio - b.prio)
    .map(({ o, cover, highlights }, i) => toPlayerOption(o, cover, highlights, i, lang));
}

/** Art styles available for a given (theme, colour) — matched on (seed, skin). */
export function availableStyles(
  catalog: Catalog,
  theme: Theme | null,
  color: ColorOption | null,
): ArtStyleOption[] {
  if (!theme || !color) return [];
  const skinId = Number(color.id);
  return catalog.artStyles.filter((s) =>
    s.compatibility.some((c) => c.seedId === theme.seedId && c.skinId === skinId),
  );
}
